import { EllipsisVerticalIcon } from "@heroicons/react/16/solid";
import { get } from "lodash-es";
import { DateTime } from "luxon";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router";

import { MenuItem, MenuList, Stack, Tooltip } from "@mui/material";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Popover from "@mui/material/Popover";
import {
  GridCellParams,
  GridColDef,
  GridFilterModel,
  GridSortModel,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid-pro";
import { GridRowSelectionModel } from "@mui/x-data-grid/models/gridRowSelectionModel";

import { AddCertificateButton } from "@pomerium/ui/components/blocks/add-certificate-button";

import DataGridPro from "../../components/DataGridPro";
import DataGridProToolbar from "../../components/DataGridProToolbar";
import DeleteActionDialog, { Entry } from "../../components/DeleteActionDialog";
import DeleteToolbarAction from "../../components/DeleteToolbarAction";
import Header from "../../components/Header";
import { LoadingError } from "../../components/LoadingError";
import Page from "../../components/Page";
import Quota from "../../components/Quota";
import SvgIcon from "../../components/styled/SvgIcon";
import useClusterColumn from "../../hooks/use-cluster-column";
import useIdColumn from "../../hooks/use-id-column";
import usePaginationModel from "../../hooks/use-pagination-model";
import { usePopover } from "../../hooks/use-popover";
import { useSavedColumnWidths } from "../../hooks/use-saved-column-widths";
import { HELP_DOCS_DEFAULT_BASE_URL } from "../../slices/help_docs";
import { useSelector } from "../../store";
import {
  getFilter,
  getOrderBy,
  useListKeyPairsQuery,
} from "../../store/enhancedApi";
import {
  KeyPairWithCertificateInfo,
  useDeleteKeyPairMutation,
} from "../../store/pomeriumApi";
import { issuerName, subjectName } from "../../utils/certificates";
import { expiresSoon } from "../../utils/date";
import AddFormDialog from "./components/AddFormDialog";
import DetailViewDialog from "./components/DetailViewDialog";
import { useIsConstrainedCluster } from "@/hooks/use-is-constrained-cluster";

type ListHeaderProps = {
  used?: number;
  quota?: number;
  allowAdd: boolean;
};

const ListHeader: FC<React.PropsWithChildren<ListHeaderProps>> = ({
  allowAdd,
}) => {
  const clusterId = useSelector((state) => state.current.clusterId);
  return (
    <Header
      title="Certificates"
      breadcrumbs={true}
      helpUrl={HELP_DOCS_DEFAULT_BASE_URL + "/docs/reference/certificates"}
      videoId="lRpkd20psbk"
      actions={
        <Stack direction="row" alignItems="center" spacing={2}>
          <Quota quota="certificates" label="user certificates" />
          {allowAdd && <AddCertificateButton namespaceId={clusterId} />}
        </Stack>
      }
    />
  );
};

const List: FC = () => {
  const clusterCol = useClusterColumn();
  const idCol = useIdColumn();
  const actionsPopover = usePopover();
  const anchorRef = useRef<HTMLElement | null>(null);
  const [certId, setCertId] = useState<string>("");
  const [selected, setSelected] = useState<GridRowSelectionModel>([]);
  const [showDelete, setShowDelete] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [keyPairId, setKeyPairId] = useState("");
  const [moreMenuItems, setMoreMenuItems] = useState(false);
  const { byId: orgById } = useSelector((state) => state.organizations);
  const { organizationId, clusterId } = useSelector((state) => state.current);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<string>(
    localStorage.getItem("certsColumns") ||
      JSON.stringify({
        id: false,
        issuer: false,
        namespaceId: false,
      }),
  );
  const { mutateColumnWidths, onColumnWidthChange } =
    useSavedColumnWidths("certificates");
  const readOnly = get(orgById, organizationId + ".readOnly", false) as boolean;
  const [deleteCert] = useDeleteKeyPairMutation();
  const params = useParams();
  const [paginationModel, setPaginationModel] = usePaginationModel();
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });

  const {
    data: result,
    error,
    isLoading,
    isFetching,
  } = useListKeyPairsQuery(
    {
      organizationId,
      namespaceId: clusterId,
      includeDescendants: true,
      offset: paginationModel.page * paginationModel.pageSize,
      limit: paginationModel.pageSize,
      filter: getFilter(filterModel),
      orderBy: getOrderBy(sortModel),
    },
    {
      skip: !organizationId || !clusterId,
    },
  );
  const certs = result?.rows || [];
  const rowCount = result?.rowCount || 0;
  const estimatedRowCount = result?.estimatedRowCount || 0;

  async function handleMultiDelete() {
    await Promise.all(
      selected.map((id) =>
        deleteCert({
          organizationId,
          keyPairId: id.toString(),
        }),
      ),
    );
  }
  const isConstrainedCluster = useIsConstrainedCluster();
  const deleteOne = () => {
    setSelected([certId]);
    setShowDelete(true);
    actionsPopover.handleClose();
  };

  useEffect(() => {
    setKeyPairId(params.certificateId || "");
    if (params.certificateId) {
      setShowDetail(true);
    } else {
      setShowDetail(false);
    }
  }, [params.certificateId]);

  useEffect(() => {
    if (certs) {
      setSelected(selected.filter((id) => certs.some((c) => c.id === id)));
    }
  }, [certs]);

  const handleCloseAddDialog = (): void => {
    setShowAddForm(false);
  };

  const handleCloseUpdateDialog = (): void => {
    setShowUpdateForm(false);
  };

  const handleOpenDetail = (id: string): void => {
    setKeyPairId(id);
    setShowDetail(true);
  };

  const handleOpenUpdate = (id: string): void => {
    setKeyPairId(id);
    setShowUpdateForm(true);
  };

  const handleCloseDetail = (): void => {
    setShowDetail(false);
    //no flicker
    setTimeout(() => setKeyPairId(""), 200);
  };

  const onCloseDeleteDialog = () => {
    setShowDelete(false);
    setSelected([]);
  };

  const CustomToolbar: FC = useCallback(
    () => (
      <Stack direction="row" justifyContent="space-between">
        <DataGridProToolbar>
          <DeleteToolbarAction
            onDelete={() => setShowDelete(true)}
            selectedIds={selected as string[]}
          />
        </DataGridProToolbar>
        <GridToolbarQuickFilter variant="outlined" size="small" sx={{ m: 1 }} />
      </Stack>
    ),
    [selected?.join("|"), setShowDelete],
  );

  let columns: GridColDef<KeyPairWithCertificateInfo>[] = [
    idCol,
    {
      display: "flex",
      headerName: "Name",
      field: "name",
      flex: 1,
    },
    {
      display: "flex",
      headerName: "Issuer",
      field: "issuer",
      flex: 1,
      valueGetter: (_value, row) => {
        return issuerName(row?.certificateInfo?.[0]);
      },
    },
    {
      display: "flex",
      headerName: "Subject",
      field: "subject",
      flex: 1,
      valueGetter: (_value, row) => {
        return subjectName(row?.certificateInfo?.[0]);
      },
    },
    clusterCol,
    {
      display: "flex",
      headerName: "Status",
      field: "status",
      flex: 1,
    },
    {
      display: "flex",
      headerName: "Expires",
      field: "notAfter",
      flex: 1,
      type: "date",
      valueGetter: (_value, row) => {
        return (
          row?.certificateInfo?.[0]?.notAfter &&
          DateTime.fromISO(row?.certificateInfo?.[0]?.notAfter).toJSDate()
        );
      },
      sortComparator: (v1, v2) => {
        return (v1 as number) - (v2 as number);
      },
      renderCell: function ExpiresCell(
        params: GridCellParams<KeyPairWithCertificateInfo>,
      ) {
        return (
          <Box
            sx={{
              color: expiresSoon(params.row?.certificateInfo?.[0]?.notAfter)
                ? "error.main"
                : "success.main",
            }}
          >
            {params.row?.certificateInfo?.[0]?.notAfter &&
              DateTime.fromISO(
                params.row?.certificateInfo?.[0]?.notAfter,
              ).toLocaleString()}
          </Box>
        );
      },
    },
    {
      display: "flex",
      field: "actions",
      headerName: "Actions",
      headerAlign: "center",
      sortable: false,
      filterable: false,
      flex: 1,
      renderCell: function action(params) {
        return (
          <Grid
            container
            spacing={0}
            direction="column"
            alignItems="center"
            justifyContent="center"
          >
            <IconButton
              onClick={(e) => {
                anchorRef.current = e.currentTarget;
                setCertId(params.row.id);
                setMoreMenuItems(params.row.origin !== "system");
                actionsPopover.handleOpen();
              }}
              sx={{ width: 4 }}
            >
              <SvgIcon color="disabled">
                <EllipsisVerticalIcon />
              </SvgIcon>
            </IconButton>
          </Grid>
        );
      },
    },
  ];
  columns = mutateColumnWidths(columns);

  if (error) {
    return (
      <Page>
        <LoadingError />
      </Page>
    );
  }

  return (
    <Page>
      <AddFormDialog
        open={showUpdateForm}
        keyPairId={keyPairId}
        certs={certs || []}
        onClose={handleCloseUpdateDialog}
      />
      <AddFormDialog
        open={showAddForm}
        onClose={handleCloseAddDialog}
        certs={[]}
      />
      <DetailViewDialog
        open={showDetail}
        keyPairId={keyPairId}
        onClose={handleCloseDetail}
      />
      {isConstrainedCluster ? null : (
        <DeleteActionDialog
          name="certificates"
          open={showDelete}
          entries={
            certs?.filter((r) => selected.includes(r.id)) || ([] as Entry[])
          }
          onClose={onCloseDeleteDialog}
          onDelete={handleMultiDelete}
        />
      )}
      <Popover
        anchorEl={anchorRef.current}
        disableScrollLock
        onClose={actionsPopover.handleClose}
        open={actionsPopover.open}
        slotProps={{
          paper: {
            sx: {
              minWidth: 260,
            },
          },
        }}
      >
        <MenuList>
          <MenuItem onClick={() => handleOpenDetail(certId)}>Details</MenuItem>
          {moreMenuItems && !readOnly && (
            <MenuItem onClick={() => handleOpenUpdate(certId)}>Edit</MenuItem>
          )}
          {!isConstrainedCluster && moreMenuItems && !readOnly && (
            <MenuItem onClick={deleteOne}>Delete</MenuItem>
          )}
          {!moreMenuItems && (
            <Tooltip title="System generated certificates cannot be deleted.">
              <span>
                <MenuItem disabled>Delete</MenuItem>
              </span>
            </Tooltip>
          )}
        </MenuList>
      </Popover>
      <ListHeader allowAdd={!isConstrainedCluster} />
      <Container component={Paper} maxWidth={false} disableGutters={true}>
        <DataGridPro
          loading={isLoading || isFetching}
          getRowHeight={() => "auto"}
          rows={clusterId ? certs || [] : []}
          columns={columns}
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setSelected(newRowSelectionModel);
          }}
          rowSelectionModel={selected}
          slots={{
            toolbar: CustomToolbar,
          }}
          columnVisibilityModel={JSON.parse(columnVisibilityModel)}
          onColumnVisibilityModelChange={(newModel) => {
            localStorage.setItem("certsColumns", JSON.stringify(newModel));
            setColumnVisibilityModel(JSON.stringify(newModel));
          }}
          isRowSelectable={(params) =>
            params.row?.origin !== "system" && !readOnly
          }
          onColumnWidthChange={onColumnWidthChange}
          paginationMode="server"
          rowCount={rowCount}
          estimatedRowCount={estimatedRowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortingMode="server"
          onSortModelChange={setSortModel}
          onFilterModelChange={setFilterModel}
        />
      </Container>
    </Page>
  );
};

export default List;
